/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印编辑次数不足提示 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-12 15:54:28
 */

import { useCallback } from 'react'
import { message, Modal } from 'antd'
import { useAppSelector } from '../../../../app/hooks'
import {
  usePayment,
  useContactUrl,
  useSensorsRole
} from '../../../../app/hooks'
import CONFIG from '../../../../constants/config'
import sensors from '../../../../utils/sensors'
import desktopLib from '../../../../utils/desktopLib'

/**
 * 水印编辑次数不足提示 Hook
 * @param sourceDetail 来源详情，用于支付弹窗（'batchRemoveWatermark' | 'singleRemoveWatermark'）
 * @returns 处理次数不足的函数
 */
export const useWatermarkTimesLimit = (
  sourceDetail: 'clickDownloadAll' | 'clickDownloadCurrentPhoto'
) => {
  const payment = usePayment()
  const contactUsUrl = useContactUrl('批量编辑水印')
  const role = useSensorsRole()
  const groupID = useAppSelector((state) => state.group.groupId)
  const vipKind = useAppSelector((state) => state.group.vipInfo?.vip_kind)

  /**
   * 处理次数不足的情况
   * @param defaultMessage 默认提示消息（用于非VIP/SVIP的情况）
   */
  const handleTimesLimit = useCallback(
    (defaultMessage?: string) => {
      if (vipKind === CONFIG.VIP_KIND.VIP) {
        // VIP会员：显示升级提示
        sensors.track('web_show_toast_remove_watermark_times_limit', {
          groupID,
          role,
          fromPlace: 'editWatermark'
        })

        message.warning({
          content: '编辑次数不足，请升级到团队超级会员',
          duration: 1,
          onClose: () => {
            payment.openSvip(sourceDetail)
          }
        })
      } else if (vipKind === CONFIG.VIP_KIND.SVIP) {
        // SVIP会员：显示联系客服提示
        sensors.track('web_get_action_pop_remove_watermark_times_depletion', {
          actionType: 'showPop',
          groupID,
          role,
          fromPlace: 'editWatermark'
        })

        Modal.confirm({
          title: '下载失败',
          content: '团队的“编辑水印/去水印”次数已用完',
          okText: '联系客服',
          cancelText: '我知道了',
          onOk: () => {
            sensors.track(
              'web_get_action_pop_remove_watermark_times_depletion',
              {
                actionType: 'contactUs',
                groupID,
                role,
                fromPlace: 'editWatermark'
              }
            )

            if (desktopLib.isDesktop) {
              desktopLib.openContact(contactUsUrl)
            } else {
              window.open(
                contactUsUrl,
                '_blank',
                'width=500,height=600,right=0,bottom=0'
              )
            }
          },
          onCancel: () => {
            sensors.track(
              'web_get_action_pop_remove_watermark_times_depletion',
              {
                actionType: 'iKnow',
                groupID,
                role,
                fromPlace: 'editWatermark'
              }
            )
          }
        })
      } else {
        // 其他情况：显示默认提示
        if (defaultMessage) {
          message.warning(defaultMessage)
        } else {
          message.warning('编辑次数不足，请充值后再试')
        }
      }
    },
    [vipKind, groupID, role, contactUsUrl, payment, sourceDetail]
  )

  return { handleTimesLimit }
}
